'use strict';

import {
  classes,
  addClassToElement,
  removeClassToElement
} from '@jspartials/class-list-utils';
import {
  TOAST_TYPE_NOTIFICATION,
  showToastNotification
} from '@jscomponents/toast-notifications';
import { kebabize } from '@jspartials/string-utils';

// Classes
const ERROR_CLASSNAME = 'js-error';
const PREFIX_FIELD_CLASSNAME = 'js-field--';
const PREFIX_ERROR_CLASSNAME = 'js-error--';
const FAST_LOGIN_NAME = 'fastlogin';

// Selectors
const ERROR_SELECTOR = `.${ERROR_CLASSNAME}`;

// Variables
const creditCardTypes = {
  visa: {
    name: 'Visa',
    pattern: /^4[0-9]{12}(?:[0-9]{3})?$/
  },
  amex: {
    name: 'Amex',
    pattern: /^3[47][0-9]{13}$/
  },
  mastercard: {
    name: 'Mastercard',
    pattern: /^5[1-5][0-9]{14}$/,
    pattern_2: /^2[2-7][0-9]{14}$/
  },
  discover: {
    name: 'Discover',
    pattern: /^6011[0-9]{12}[0-9]*$/,
    pattern_2: /^62[24568][0-9]{13}[0-9]*$/,
    pattern_3: /^6[45][0-9]{14}[0-9]*$/
  },
  diners: {
    name: 'Diners Club',
    pattern: /^3[0689][0-9]{12}[0-9]*$/
  },
  jcb: {
    name: 'JCB',
    pattern: /^35[0-9]{14}[0-9]*$/
  },
  cup: {
    name: 'CUP',
    pattern: /^62[0-9]{14}[0-9]*$/,
    pattern_2: /^81[0-9]{14}[0-9]*$/
  },
  maestro: {
    name: 'Maestro',
    pattern: /^(5[06-8]|6\d)\d{14}(\d{2,3})?$/
  }
};

// Functions

// Toggle input type for password
const togglePasswordVisibility = (input) => {
  if (input.type === 'password') {
    input.type = 'text';
  } else {
    input.type = 'password';
  }
};

// Clean all errors
const cleanAllErrors = () => {
  const fieldsWithErrors = document.querySelectorAll(`.${classes.has.error}`);
  const errorFields = document.querySelectorAll(ERROR_SELECTOR);

  if (fieldsWithErrors) {
    fieldsWithErrors.forEach((field) =>
      removeClassToElement(field, classes.has.error)
    );
  }

  if (errorFields) {
    errorFields.forEach((field) => (field.innerHTML = ''));
  }
};

// Handle errors
const handleErrors = (errors) => {
  Object.entries(errors).forEach((error) => {
    const [name, messages] = error;
    const field = document.querySelector(
      `.${PREFIX_FIELD_CLASSNAME}${kebabize(name)}`
    );
    const errorField = document.querySelector(
      `.${PREFIX_ERROR_CLASSNAME}${kebabize(name)}`
    );

    if (messages.length > 0) {
      if (name.toLowerCase().includes(FAST_LOGIN_NAME)) {
        if (field) {
          addClassToElement(field, classes.has.error);
        }
      } else if (errorField) {
        if (name.toLowerCase().includes(TOAST_TYPE_NOTIFICATION)) {
          messages.forEach((message) =>
            showToastNotification(errorField, { message: message })
          );
        } else {
          messages.forEach((message) => addErrorMessage(errorField, message));

          if (field) {
            addClassToElement(field, classes.has.error);
          }
        }
      }
    }
  });
};

/**
 * Scroll to first error element
 * @param {NodeList} allErrors - All error elements
 * @return {void}
 */
const scrollToFirstError = (allErrors) => {
  const firstError = Array.from(allErrors).find(
    (error) => error.textContent.trim().length > 0
  );
  const firstErrorParent = firstError ? firstError.parentElement : null;

  if (firstErrorParent) {
    firstErrorParent.scrollIntoView({
      behavior: 'smooth'
    });
  }
};

// Add error message to element
const addErrorMessage = (element, message) => {
  element.appendChild(
    Object.assign(document.createElement('p'), { textContent: message })
  );
};

// Count errors
const countErrors = (object) =>
  Object.keys(object).reduce((acc, key) => acc + object[key].length, 0);

// Format credit card expiration date
const formatCreditCardExpirationDate = (input) => {
  const creditCardExpiration = input.value;
  const code = event.keyCode;
  const allowedKeys = [8];

  if (allowedKeys.indexOf(code) !== -1) {
    return;
  }

  input.value = creditCardExpiration
    .replace(
      /^([1-9]\/|[2-9])$/g,
      '0$1/' // 3 > 03/
    )
    .replace(
      /^(0[1-9]|1[0-2])$/g,
      '$1/' // 11 > 11/
    )
    .replace(
      /^([0-1])([3-9])$/g,
      '0$1/$2' // 13 > 01/3
    )
    .replace(
      /^(0?[1-9]|1[0-2])([0-9]{2})$/g,
      '$1/$2' // 141 > 01/41
    )
    .replace(
      /^([0]+)\/|[0]+$/g,
      '0' // 0/ > 0 and 00 > 0
    )
    .replace(
      /[^\d\/]|^[\/]*$/g,
      '' // To allow only digits and `/`
    )
    .replace(
      /\/\//g,
      '/' // Prevent entering more than 1 `/`
    );
};

// Obtain credit card type from number
const creditCardType = (creditCardNumber) => {
  if (creditCardIsVisa(creditCardNumber)) {
    return creditCardTypes.visa.name;
  }

  if (creditCardIsAmex(creditCardNumber)) {
    return creditCardTypes.amex.name;
  }

  if (creditCardIsMastercard(creditCardNumber)) {
    return creditCardTypes.mastercard.name;
  }

  if (creditCardIsDiscover(creditCardNumber)) {
    return creditCardTypes.discover.name;
  }

  if (creditCardIsDinersClub(creditCardNumber)) {
    return creditCardTypes.diners.name;
  }

  if (creditCardIsJcb(creditCardNumber)) {
    return creditCardTypes.jcb.name;
  }

  if (creditCardIsCup(creditCardNumber)) {
    return creditCardTypes.cup.name;
  }

  if (creditCardIsMaestro(creditCardNumber)) {
    return creditCardTypes.maestro.name;
  }

  return '';
};

// Check if credit card is VISA type
const creditCardIsVisa = (creditCardNumber) => {
  const regExp = new RegExp(creditCardTypes.visa.pattern);
  return regExp.test(creditCardNumber);
};

// Check if credit card is American Express (AMEX) type
const creditCardIsAmex = (creditCardNumber) => {
  const regExp = new RegExp(creditCardTypes.amex.pattern);
  return regExp.test(creditCardNumber);
};

// Check if credit card is MASTERCARD type
const creditCardIsMastercard = (creditCardNumber) => {
  const regExp = new RegExp(creditCardTypes.mastercard.pattern);
  const regExp2 = new RegExp(creditCardTypes.mastercard.pattern_2);
  return regExp.test(creditCardNumber) || regExp2.test(creditCardNumber);
};

// Check if credit card is Discover type
const creditCardIsDiscover = (creditCardNumber) => {
  const regExp = new RegExp(creditCardTypes.discover.pattern);
  const regExp2 = new RegExp(creditCardTypes.discover.pattern_2);
  const regExp3 = new RegExp(creditCardTypes.discover.pattern_3);
  return (
    regExp.test(creditCardNumber) ||
    regExp2.test(creditCardNumber) ||
    regExp3.test(creditCardNumber)
  );
};

// Check if credit card is Diners Club type
const creditCardIsDinersClub = (creditCardNumber) => {
  const regExp = new RegExp(creditCardTypes.diners.pattern);
  return regExp.test(creditCardNumber);
};

// Check if credit card is JCB type
const creditCardIsJcb = (creditCardNumber) => {
  const regExp = new RegExp(creditCardTypes.jcb.pattern);
  return regExp.test(creditCardNumber);
};

// Check if credit card is CUP (China Union Pay) type
const creditCardIsCup = (creditCardNumber) => {
  const regExp = new RegExp(creditCardTypes.cup.pattern);
  const regExp2 = new RegExp(creditCardTypes.cup.pattern_2);
  return regExp.test(creditCardNumber) || regExp2.test(creditCardNumber);
};

// Check if credit card is Maestro type
const creditCardIsMaestro = (creditCardNumber) => {
  const regExp = new RegExp(creditCardTypes.maestro.pattern);
  return regExp.test(creditCardNumber);
};

export {
  togglePasswordVisibility,
  cleanAllErrors,
  handleErrors,
  scrollToFirstError,
  addErrorMessage,
  countErrors,
  creditCardTypes,
  formatCreditCardExpirationDate,
  creditCardType
};
